import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { Navigate, Route, BrowserRouter as Router, Routes } from 'react-router-dom';
import { Toaster } from 'sonner';

import { RequireAuth } from './components/RequireAuth';
import { useMe } from './features/auth/queries';
import { ReviewPage } from './features/review/ReviewPage';
import { ReviewsPage } from './features/reviews/ReviewsPage';
import { Analytics } from './pages/Analytics';
import { Dashboard } from './pages/Dashboard';
import { FlowEtfSettings } from './pages/FlowEtfSettings';
import { Help } from './pages/Help';
import { Import } from './pages/Import';
import { Investing } from './pages/Investing';
import { Journal } from './pages/Journal';
import { JournalEntry } from './pages/JournalEntry';
import { Login } from './pages/Login';
import { MarketConsole } from './pages/MarketConsole';
import { MarketCrowding } from './pages/MarketCrowding';
import { MarketDataSettings } from './pages/MarketDataSettings';
import { MarketDebrief } from './pages/MarketDebrief';
import { MarketFlow } from './pages/MarketFlow';
import { MarketHealth } from './pages/MarketHealth';
import { MarketNow } from './pages/MarketNow';
import { MarketPulse } from './pages/MarketPulse';
import { MarketScreener } from './pages/MarketScreener';
import { MarketStocks } from './pages/MarketStocks';
import { OAuthConsent } from './pages/OAuthConsent';
import { PositionLifecycle } from './pages/PositionLifecycle';
import { Positions } from './pages/Positions';
import { Register } from './pages/Register';
import { Rules } from './pages/Rules';
import { Settings } from './pages/Settings';
import { StockGroupSettings } from './pages/StockGroupSettings';
import { StockScanSettings } from './pages/StockScanSettings';
import { SymbolLots } from './pages/SymbolLots';
import { TradePlan } from './pages/TradePlan';
import { WeeklyReview } from './pages/WeeklyReview';
import { WeeklyReviews } from './pages/WeeklyReviews';

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      refetchOnReconnect: false,
      retry: 1,
      staleTime: 30_000,
    },
  },
});

function RootRedirect() {
  const me = useMe();

  if (me.isLoading) {
    return <main className="p-6 text-text-muted">Loading...</main>;
  }

  return <Navigate to={me.isSuccess ? '/dashboard' : '/login'} replace />;
}

export function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <Router>
        <Routes>
          <Route path="/" element={<RootRedirect />} />
          <Route path="/login" element={<Login />} />
          <Route path="/register" element={<Register />} />
          <Route path="/oauth/consent" element={<OAuthConsent />} />
          <Route element={<RequireAuth />}>
            <Route path="/dashboard" element={<Dashboard />} />
            <Route path="/analytics" element={<Analytics />} />
            <Route path="/analytics/symbols/:symbol" element={<SymbolLots />} />
            <Route path="/help" element={<Help />} />
            <Route path="/import" element={<Import />} />
            <Route path="/investing" element={<Investing />} />
            <Route path="/journal" element={<Journal />} />
            <Route path="/journal/:id" element={<JournalEntry />} />
            <Route path="/market-console" element={<MarketConsole />}>
              <Route index element={<Navigate to="health" replace />} />
              <Route path="health" element={<MarketHealth />} />
              <Route path="now" element={<MarketNow />} />
              <Route path="debrief" element={<MarketDebrief />} />
              <Route path="pulse" element={<MarketPulse />} />
              <Route path="crowding" element={<MarketCrowding />} />
              <Route path="stocks" element={<MarketStocks />} />
              <Route path="screener" element={<MarketScreener />} />
              <Route path="flow" element={<MarketFlow />} />
            </Route>
            <Route
              path="/market-health"
              element={<Navigate to="/market-console/health" replace />}
            />
            <Route path="/positions" element={<Positions />} />
            <Route path="/positions/:symbol" element={<PositionLifecycle />} />
            <Route path="/rules" element={<Rules />} />
            <Route path="/settings" element={<Settings />} />
            <Route path="/settings/market-data" element={<MarketDataSettings />} />
            <Route path="/settings/flow-etfs" element={<FlowEtfSettings />} />
            <Route path="/settings/stock-scan" element={<StockScanSettings />} />
            <Route path="/settings/stock-groups" element={<StockGroupSettings />} />
            <Route path="/trade-plan" element={<TradePlan />} />
            <Route path="/reviews" element={<ReviewsPage />} />
            <Route path="/reviews/:tradeId" element={<ReviewsPage />} />
            <Route path="/weekly-review" element={<Navigate to="/reviews/weekly" replace />} />
            <Route path="/reviews/weekly" element={<WeeklyReviews />} />
            <Route path="/reviews/weekly/:weekStart" element={<WeeklyReview />} />
            <Route path="/reviews/trades/:tradeId" element={<ReviewPage />} />
          </Route>
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </Router>
      <Toaster closeButton duration={5_000} richColors visibleToasts={3} />
    </QueryClientProvider>
  );
}
